import {HubOutlined as TraceIcon} from "@mui/icons-material";
import React, { useEffect } from "react";
import parkourImage from "../../assets/ParkourLogo.svg";
import css_self from "./css/NavBar.module.scss";
import "./css/NavBar.scss";
import { buildPositionTree } from "./positionTree";
import PositionTreeGuides, { ROW_PADDING } from "./PositionTreeGuides";
import {
    Tooltip, Avatar, Box, Button, IconButton, Menu, MenuItem,
    Popover, Typography, Link, Snackbar, Alert
} from "@mui/material";
import {
    Blue3, Grey2, Grey17, Grey6, White, Grey5, Grey4, Blue4
} from "../../theme/styles";
import { useNavigate } from "react-router-dom";
import {AccountTreeOutlined, ArticleOutlined, CodeOutlined, HelpOutlineOutlined, Hive, Language, LockResetOutlined, Logout, MedicalServicesOutlined, PermIdentity, Settings, SupervisorAccount, SwitchAccount as SwitchAccountIcon, Sync, SyncAlt, ThumbUp, WorkOutlined as WorkOutline} from "@mui/icons-material";
import useAppContext from "../../util/AppContext";
import EventBus from "../../util/EventBus";
import { Account, SessionContext, hasProduct} from "../../util/SessionContext";
import { buildSsoHandoffUrl , handoffVisibility} from "../../util/ssoAuthenticate";
// MqttClient hook keeps the WSS connection alive while logged in and exposes the
// notification push signal used by the bell + toast below.
import { NotificationPreview } from "../../util/MqttClient";
import AssistantChat from "../Assistant/AssistantChat";
import { CURRENT_APPLICATION } from "../../data/notifications";
import css_buttons from "../Common/css/CCButton.module.scss";
import axios from "axios";
import { SwitchAccount } from "../Login2/SwitchAccount";
import Login2 from "../Login2/Login2";
import ChangePasswordDialog from "../Login2/ChangePasswordDialog";

type MenuSelection = { id: string; target: EventTarget & HTMLButtonElement };

export default function NavBar() {
    const navigate = useNavigate();

    const [showLoginDialog, setShowLoginDialog] = React.useState<boolean>(false);
    const [showSwitchAccount, setShowSwitchAccount] = React.useState<boolean>(false);
    const [showChangePassword, setShowChangePassword] = React.useState<boolean>(false);
    const [menu, setMenu] = React.useState<MenuSelection | null>(null);

    const sessionContext: SessionContext = useAppContext()?.sessionContext!;

    // NO MQTT IN THIS TIER. cres-aws-t10 has no IoT topic rule, no custom MQTT domain and no
    // authorizer for one -- the module was deliberately left out when the tier was stood up,
    // because Reporting pushes nothing. The hook was still mounted and REACT_APP_MQTT_ENDPOINT is
    // undefined here, so it dialled wss://undefined/mqtt and retried forever.
    const notificationSignal = 0;
    const latestNotification = null as NotificationPreview | null;

    // Toast the newest notification as its MQTT push arrives. The bell uses the
    // same signal to refetch its list/unread-count.
    const [notifToast, setNotifToast] = React.useState<NotificationPreview | null>(null);
    const lastSignalRef = React.useRef<number>(0);
    React.useEffect(() => {
        if (notificationSignal > lastSignalRef.current) {
            lastSignalRef.current = notificationSignal;
            const n = latestNotification;
            // Only toast notifications meant for this app (or for all apps).
            if (n && (n.applications === null || n.applications.includes(CURRENT_APPLICATION))) {
                setNotifToast(n);
            }
        }
    }, [notificationSignal, latestNotification]);

    useEffect(() => {
        const sessionUpdateListener = () => {
            setShowLoginDialog(!!sessionContext.getShowLoginDialog());
        };

        EventBus.session.addListener("update", sessionUpdateListener);
        EventBus.components.addListener("switch-account-close", onSwitchAccountClose);

        sessionUpdateListener();

        return () => {
            EventBus.session.removeListener("update", sessionUpdateListener);
            EventBus.components.removeListener("switch-account-close", onSwitchAccountClose);
        };
    }, [sessionContext]);

    function onSwitchAccountClose() {
        setShowSwitchAccount(false);
    }

    const content = (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                paddingLeft: 20,
                height: "50px",
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: Grey2,
                backgroundColor: White
            }}>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", flexShrink: 0 }}>
                    <div style={{ paddingTop: "0px", paddingRight: "2px", marginLeft: "-5px", cursor: "pointer" }} onClick={() => navigate("/")}>
                        <img className={css_self.ParkourLogo} src={parkourImage} alt="Home" />
                    </div>
                    <div style={{ paddingTop: "5px", marginLeft: "57px", marginRight: "4px" }}>
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Grey4 }}>|</Typography>
                    </div>
                    <Box sx={{ paddingTop: "7px", cursor: "pointer",
                              display: { xs: "none", sm: "block" } }} onClick={() => navigate("/")}>
                        <Typography style={{ fontSize: 24, fontWeight: 500, color: Blue4, whiteSpace: "nowrap" }}>
                            &nbsp;&nbsp;Parkour-AI
                        </Typography>
                    </Box>
                    <Box sx={{ paddingTop: "5px", marginLeft: "15px", marginRight: "4px",
                               display: { xs: "none", sm: "block" } }}>
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Grey4 }}>|</Typography>
                    </Box>
                    <div style={{ paddingTop: "7px", cursor: "pointer" }} onClick={() => navigate("/")}>
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Blue3, whiteSpace: "nowrap" }}>
                            &nbsp;&nbsp;Reporting
                        </Typography>
                    </div>
                </div>

                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    flexGrow: 1,
                    minWidth: 0,
                    paddingTop: "15px",
                    marginLeft: "30px"
                }}>
                    {sessionContext.valid() ? (
                        <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey5 }}>
                            Welcome: <b>{sessionContext.getAccountName()}!</b>
                        </Typography>
                    ) : null}
                </div>

                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    minWidth: { xs: 0, sm: "260px" },
                    paddingTop: "4px"
                }}>
                    {/* NO MQTT PROBE INDICATOR. It was a grey dot that turns green on the first
                        test probe -- in a tier with no MQTT at all it stays grey forever and reads
                        as something still connecting. */}
                    {/* NO BELL. Notifications are served by t5/v1/notifications, which this tier
                        does not have and whose token it no longer carries. */}
                    {sessionContext.valid() && (
                        <div style={{ width: "45px", paddingTop: "2px" }}>
                            <AssistantChat />
                        </div>
                    )}
                    <div style={{ width: "45px", paddingTop: "2px" }}>
                        {renderSiteNavigation()}
                    </div>
                    {sessionContext.valid() && (
                        <div style={{ width: "45px", paddingTop: "3px" }}>
                            {renderVisibilityNavigation()}
                        </div>
                    )}
                    <div style={{ width: "45px", paddingTop: "1px" }}>
                        {renderAccountNavigation()}
                    </div>
                </Box>
            </div>
        </div>
    );

    return (
        <>
            {showSwitchAccount ? <SwitchAccount /> : null}
            {showChangePassword ? <ChangePasswordDialog onClose={() => setShowChangePassword(false)}/> : null}
            {content}
            {showLoginDialog ? <Login2 /> : null}
            <Snackbar
                open={!!notifToast}
                autoHideDuration={8000}
                onClose={() => setNotifToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    onClose={() => setNotifToast(null)}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                if (notifToast?.url) navigate(notifToast.url);
                                setNotifToast(null);
                            }}
                        >
                            View
                        </Button>
                    }
                    sx={{ alignItems: "center", maxWidth: 420 }}
                >
                    <b>{notifToast?.title}</b>
                    {notifToast?.body ? `: ${notifToast.body}` : ""}
                </Alert>
            </Snackbar>
        </>
    );

    // ─────────────────────────────────────────────────────────────
    // Render Functions
    // ─────────────────────────────────────────────────────────────

    function renderSiteNavigation() {
        const goToSite = (route: string, externalUrl?: string) => {
            if (externalUrl) {
                // Hand off this app's access token so the target can mint a native SSO session, plus
                // the active persona so a persona-aware target (t1/t4/t5) honors it instead of prompting.
                const accessToken = sessionContext.valid() ? sessionContext.getAccessToken() : null;
                // THE ACTIVE ACCOUNT AND PERSONA ARE OFFERED, not imposed. The exchange verifies the
                // persona against the list it would itself have offered and refuses when it does not
                // belong, and the target falls back to asking -- so the hint can only save a question,
                // never answer one wrongly. The account half is safe outright: the suite is one database
                // partitioned by realm, so an account USID means the same thing on both sides.
                window.open(buildSsoHandoffUrl(externalUrl, accessToken, handoffVisibility(sessionContext)), "_blank", "noopener,noreferrer");
            } else {
                navigate(route);
            }
            setMenu(null);
        };

        const siteCard = (icon: React.ReactNode, title: string, description: string, route: string, externalUrl?: string) => (
            <>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <IconButton
                        className={css_buttons.blueIconButton}
                        style={{ height: "72px", width: "72px" }}
                        onClick={() => goToSite(route, externalUrl)}
                    >
                        {icon}
                    </IconButton>
                    <div style={{ marginLeft: "10px" }}>
                        <Link
                            underline="none"
                            variant="inherit"
                            color="inherit"
                            href="#"
                            onClick={(e) => { e.preventDefault(); goToSite(route, externalUrl); }}
                        >
                            <Typography style={{ fontSize: "22px", fontWeight: 500, color: "#000000", textAlign: "left" }}>
                                {title}
                            </Typography>
                        </Link>
                    </div>
                </div>
                <div style={{ marginTop: "6px" }}>
                    <Typography style={{ fontSize: "13px", fontWeight: "400", color: Grey5, textAlign: "left" }}>
                        {description}
                    </Typography>
                </div>
            </>
        );

        const iconSx = { color: Blue3, width: "54px", height: "54px" };
        const apps = [
            ...(hasProduct("administration")
                ? [siteCard(<Settings sx={iconSx} />, "Administration", "Manage users, roles, tenants, and suite-wide configuration.", "/administration", process.env.REACT_APP_ADMINISTRATION_URL || "https://admin.cres-intelligence.io")]
                : []),
            // GATED ON `tmf` (2026-11-04), like every other tile. It was drawn for everybody and pointed at a route no app
            // serves; the server offers Parkour-AI eTMF (t13) to nobody until it has privileges to gate on (products.py).
            ...(hasProduct("tmf")
                ? [siteCard(<MedicalServicesOutlined sx={iconSx} />, "Clinical Trials", "Plan, execute, and report on clinical studies — protocols, sites, participants, and outcomes.", "/clinical-trials", process.env.REACT_APP_TMF_URL || "https://tmf.cres-intelligence.io")]
                : []),
            ...(hasProduct("developer")
                ? [siteCard(<CodeOutlined sx={iconSx} />, "Developer", "API explorer, SDKs, and documentation for building integrations.", "/developer", process.env.REACT_APP_DEVELOPER_URL || "https://developer.cres-intelligence.io")]
                : []),
            ...(hasProduct("qms")
                ? [siteCard(<Hive sx={iconSx} />, "Foundation", "Core platform services, identity, and shared building blocks for the suite.", "/foundation", process.env.REACT_APP_FOUNDATION_URL || "https://foundation.cres-intelligence.io")]
                : []),
            // GATED ON `docs`, like every other product tile: the launcher must not offer a door
            // the other end refuses. The server settles which products are open at login
            // (cres/utils/products.py) -- any privilege over documents opens Docs, which is how a
            // document-control clerk reaches it with what they already hold, and how an external
            // reviewer holding only `document:author` gets a launcher with Docs and nothing else.
            ...(hasProduct("docs")
                ? [siteCard(<ArticleOutlined sx={iconSx} />, "Parkour-AI Documents", "Author and review the text of controlled documents \u2014 collaboratively, with suggestions and approvals.", "/documents", process.env.REACT_APP_DOCS_URL || "https://docs.cres-intelligence.io")]
                : []),
            ...(hasProduct("trace")
                ? [siteCard(<TraceIcon sx={iconSx} />, "Parkour-AI Trace", "Requirements, risks, tests and releases of regulated software, linked into one traceable record.", "/", process.env.REACT_APP_TRACE_URL || "https://trace.cres-intelligence.io")]
                : []),
            // GATED ON `sbom` (2026-11-02), like every other product tile: SBOM is now described in products.py, and its
            // sign-in refuses a person with no SBOM grant, so the launcher must not offer them its door.
            ...(hasProduct("sbom")
                ? [siteCard(<AccountTreeOutlined sx={iconSx} />, "SBOM", "Software Bill of Materials — manage component inventories and licenses.", "/sbom", process.env.REACT_APP_SBOM_URL || "https://sbom.cres-intelligence.io")]
                : []),
        ];

        return (
            <div>
                <Tooltip title="Sites Portfolio" placement="bottom">
                    <IconButton
                        className={css_self.NavMenuButton}
                        style={{ marginTop: "-1px" }}
                        onClick={(e) => setMenu({ id: "sites", target: e.currentTarget })}
                    >
                        <Language />
                    </IconButton>
                </Tooltip>

                {menu?.id === "sites" && (
                    <Popover
                        anchorEl={menu.target}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: -15, horizontal: "left" }}
                        open={Boolean(menu.target)}
                        onClose={() => setMenu(null)}
                        slotProps={{
                            paper: { elevation: 2 },
                        }}
                    >
                        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                            <div style={{ minWidth: "320px", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
                                {apps.map((card, idx) => (
                                    <div key={idx} style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-start",
                                        marginBottom: "10px",
                                        border: "1px solid #EDEFF3",
                                        borderRadius: "10px",
                                        padding: "15px",
                                        boxSizing: "border-box"
                                    }}>
                                        {card}
                                    </div>
                                ))}
                            </div>
                        </Box>
                    </Popover>
                )}
            </div>
        );
    }

    function renderVisibilityNavigation() {
        const account: Account = sessionContext.getAccount()!;
        if (!account) return null;

        return (
            <>
                <Tooltip title="Personas" placement="bottom">
                    <IconButton
                        className={css_self.NavMenuButton}
                        style={{ marginTop: "-1px" }}
                        onClick={(e) => setMenu({ id: "visibility", target: e.currentTarget })}
                    >
                        <SyncAlt />
                    </IconButton>
                </Tooltip>
                {menu?.id === "visibility" && (
                    <Menu
                        anchorEl={menu.target}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: -17, horizontal: "right" }}
                        open={Boolean(menu.target)}
                        onClose={() => setMenu(null)}
                        slotProps={{
                            paper: { elevation: 2 },
                            list: {
                                disablePadding: true,
                                style: { minWidth: "300px" },
                            }
                        }}>
                        {account.Positions.length > 0 ? (
                            <>
                                <MenuItem key="position-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>Positions</Typography>
                                    </div>
                                </MenuItem>
                                {buildPositionTree(account.Positions, account.PositionAncestors ?? []).map(row => (
                                    <MenuItem key={row.node.USID} className={css_self.Standard}
                                              // Stretch, so the guide columns are full row height and
                                              // their borders meet the rows above and below.
                                              sx={{ alignItems: "stretch",
                                                    paddingTop: `${ROW_PADDING}px`,
                                                    paddingBottom: `${ROW_PADDING}px` }}
                                              disabled={!row.selectable}
                                              onClick={row.selectable ? () => {
                                                  setMenu(null);
                                                  sessionContext.setActiveVisibility("Position", row.node.USID);
                                                  EventBus.visibility.emit("change-visibility", "Position");
                                              } : undefined}>&emsp;&emsp;
                                        <PositionTreeGuides row={row} />
                                        <SupervisorAccount style={{ color: row.selectable ? Grey17 : Grey6,
                                                                    alignSelf: "center" }} />
                                        <div style={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", fontWeight: "400",
                                                                 color: row.selectable ? Grey17 : Grey6 }}>
                                                {row.node.Name}
                                            </Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                        {account.UserGroups.length > 0 ? (
                            <>
                                <MenuItem key="userGroup-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>User Groups</Typography>
                                    </div>
                                </MenuItem>
                                {account.UserGroups.map(userGroup => (
                                    <MenuItem key={userGroup.USID} className={css_self.Standard} onClick={() => {
                                        setMenu(null);
                                        sessionContext.setActiveVisibility("User Group", userGroup.USID);
                                        EventBus.visibility.emit("change-visibility", "User Group");
                                    }}>&emsp;&emsp;
                                        <ThumbUp style={{ color: Grey17 }} />
                                        <div style={{ display: "block", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>{userGroup.Name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                        {account.WorkGroups.length > 0 ? (
                            <>
                                <MenuItem key="workgroup-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>Work Groups</Typography>
                                    </div>
                                </MenuItem>
                                {account.WorkGroups.map(workgroup => (
                                    <MenuItem key={workgroup.USID} className={css_self.Standard} onClick={() => {
                                        setMenu(null);
                                        sessionContext.setActiveVisibility("Work Group", workgroup.USID);
                                        EventBus.visibility.emit("change-visibility", "Work Group");
                                    }}>&emsp;&emsp;
                                        <WorkOutline style={{ color: Grey17 }} />
                                        <div style={{ display: "block", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>{workgroup.Name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                    </Menu>
                )}
            </>
        );
    }

    function stringAvatar(name: string | null | undefined) {
        // A SIGNED-IN SESSION WITH NO NAME BLANKED THE WHOLE APP. This is the avatar's two
        // initials, and it ran unguarded on whatever getName() returned -- null throws inside
        // .split, the error escapes render, and React unmounts the tree. The page went white with
        // one line in the console and nothing on screen.
        //
        // An account without a name is odd rather than impossible -- an SSO profile that carried
        // no display name would do it -- and a missing avatar is a far better answer than a
        // missing application.
        return (name ?? "")
            .split(" ")
            .map(word => word[0] ?? "")
            .join("")
            .substring(0, 2);
    }

    function renderAccountNavigation() {
        if (!sessionContext.valid()) {
            return (
                <IconButton
                    // NAMED, because it is the only way into this app and it was an icon with no
                    // accessible name at all -- unreachable by a screen reader, unfindable by role,
                    // and on a landing page whose own text says "Sign in to continue".
                    aria-label="Sign in"
                    className={css_self.NavMenuButton}
                    onClick={() => {
                        sessionContext.setShowLoginDialog?.(true);
                        setShowLoginDialog(true);
                    }}
                >
                    <PermIdentity />
                </IconButton>
            );
        }

        return (
            <div>
                <Button
                    className={css_self.NavMenuButton}
                    variant="text"
                    onClick={(e) => setMenu({ id: "account", target: e.currentTarget })}
                >
                    <Avatar sx={{ marginTop: "2px", bgcolor: Blue3, width: 35, height: 35 }}>
                        {stringAvatar(sessionContext.getName())}
                    </Avatar>
                </Button>
                {menu?.id === "account" && (
                    <Menu
                        anchorEl={menu.target}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: -16, horizontal: "center" }}
                        open={Boolean(menu.target)}
                        onClose={() => setMenu(null)}
                        slotProps={{
                            paper: { elevation: 2 },
                            list: {
                                disablePadding: true,
                                style: { minWidth: "200px" },
                            }
                        }}>
                        <MenuItem disabled>
                            <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey17 }}>
                                Account: <b>{sessionContext.getAccountName()}</b>
                            </Typography>
                        </MenuItem>
                        <MenuItem disabled>
                            <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey17 }}>
                                Hello, <b>{sessionContext.getName()}</b>
                            </Typography>
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); navigate("/help"); }}>
                            <HelpOutlineOutlined style={{ color: Grey6, marginRight: 8 }} />
                            Help
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); setShowChangePassword(true); }}>
                            <LockResetOutlined style={{ color: Grey6, marginRight: 8 }} />
                            Change password
                        </MenuItem>
                        <MenuItem divider onClick={() => { setShowSwitchAccount(true); setMenu(null); }}>
                            <SwitchAccountIcon style={{ color: Grey6, marginRight: 8 }} />
                            Switch Account
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); refresh(); }}>
                            <Sync style={{ color: Grey6, marginRight: 8 }} />
                            Refresh
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); navigate("/logout"); }}>
                            <Logout style={{ color: Grey6, marginRight: 8 }} />
                            Log out
                        </MenuItem>
                    </Menu>
                )}
            </div>
        );
    }


    async function refresh() {
        axios({
            method: "PUT",
            baseURL: process.env.REACT_APP_API_URL,
            url: "t10/v1/authenticate",
            data: {
                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
            },
        }).then((response) => {
            // Persist the refreshed session so administrative-data changes (new Positions / User
            // Groups / Work Groups, roles, privileges) appear WITHOUT a full logout/login. Previously
            // the response was discarded and the page just reloaded, re-reading stale sessionStorage.
            // Active persona (ActiveVisibility) is intentionally preserved. Mirrors Login2 storage.
            sessionContext.setIdentityToken(response.data["IdentityToken"]);
            sessionContext.setSessionUSID(response.data["Session"]);
            sessionContext.setAccessToken(response.data["AccessToken"]);
            sessionContext.setRefreshToken(response.data["RefreshToken"]);
            sessionContext.setAccount(response.data["Account"]);
            sessionContext.setAccountName(response.data["Account"]["AccountName"]);
            sessionContext.setAccountUserUSID(response.data["Account"]["AccountUserUSID"]);
            sessionContext.setRoles(response.data["Roles"]);
            sessionContext.setPrivileges(response.data["Privileges"]);
            sessionContext.setProducts(response.data["Products"] || []);
            sessionContext.setPrivilegeConstraints(response.data["PrivilegeConstraints"]);
            sessionContext.setTenants(response.data["Tenants"]);
            sessionContext.setPickValues(response.data["PickValues"]);
            window.location.href = "/";
        }).catch(() => {
            EventBus.error.emit("show-system-error");
        });
    }
}
